import { fabric } from 'fabric';
import { type RecordId, type Constructor } from '../types';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import type Connector from './Connector';
import { hasLabelBehaviors, isConnector, isShape } from './guards';
import { type ShapeName } from '../shapes';
import type ShapeBase from '../shapes/ShapeBase';
import type EmbeddedTextBehaviors from './EmbeddedTextBehaviorsV2';
import type Canvas from './Canvas';

// For a shape to act as a connector label, the shapeName must appear here as well
// as the main Shape object using the LabelBehaviors decorator
export const labelShapes: ShapeName[] = ['text', 'icon'];

/** Checks if an object is currently an active label */
export const isConnectorLabel = (
  obj: fabric.Object
): obj is LabelBehaviors & fabric.Object & { connectorId: RecordId } =>
  hasLabelBehaviors(obj) && !!obj.connectorId;

// Behaviors for objects that can become labels (currently ShapeText)
interface LabelBehaviors {
  initializeLabelBehaviors(): void;
  clearLabelBehaviors(): void;
  clearLabelBehaviors(): void;
  addConnectorLabel(): void;
  updateLabelPositionAndAngle(connector: Connector): void;
  initLabel(connector: Connector, t?: number | null, angle?: number): void;
  labelPosition: number | null;
}
// eslint-disable-next-line @typescript-eslint/no-redeclare
function LabelBehaviors(
  Base: Constructor<fabric.Object & EmbeddedTextBehaviors>
): any {
  type Orientation = 'above' | 'below' | 'left' | 'right' | 'inline';
  return class extends Base implements LabelBehaviors {
    __hasLabelBehaviors = true;
    // Id of connector this label belongs to
    connectorId: RecordId | undefined | null;
    // Position of label on line, represented as value between 0 and 1
    labelPosition: number | null;
    // Placement relative to connector (inline/above/below)
    labelOrientation: Orientation;
    // Angle relative to connector, most notably for conforming labels
    angle: number;

    initializeLabelBehaviors() {
      this.on('moving', this.dragLabel);
      this.on('added', this.addConnectorLabel);
    }

    clearLabelBehaviors() {
      this.off('moving', this.dragLabel);
    }

    // Attach to connector when label is added or canvas is reloaded
    addConnectorLabel() {
      if (this.connectorId) {
        const connector = this.controller.dataStoreObjects.findRecordById(
          this.connectorId
        );
        if (!connector || !isConnector(connector)) {
          console.error(
            `Expected to find connector ${this.connectorId} for label ${this.id}, found none`
          );
          this.clearLabel();
          return;
        }

        this.initLabel(connector, this.labelPosition, this.angle);
      }
    }

    removeConnectorLabel() {
      this.connector?.removeLabel(this);
      this.connector = null;
    }

    // When the connector moves, update position and angle
    updateLabelPositionAndAngle(connector: Connector) {
      let { x, y, angle } = connector.router.pointOnLine(
        connector,
        this.labelPosition!
      );

      // Divide dimensions by two to shift object such that (x,y) is centered
      const adjustedScaledHeight = this.getScaledHeight() / 2;
      const adjustScaledWidth = this.getScaledWidth() / 2;

      // Flip text at 90 degrees so that text is always right side up
      angle = this.useUprightAngle(angle);

      // Label placement when angle is 0
      let top = y - adjustedScaledHeight;
      let left = x - adjustScaledWidth;

      if (connector.alignLabels) {
        // Based on rotation formula:
        // 𝑥′= 𝑥 * cos(𝜃) − 𝑦 * sin(𝜃)
        // 𝑦′= 𝑥 * sin(𝜃) + 𝑦 * cos(𝜃)
        const rads = fabric.util.degreesToRadians(angle);
        const sinA = Math.sin(rads);
        const cosA = Math.cos(rads);

        top = y - (adjustedScaledHeight * cosA + adjustScaledWidth * sinA);
        left = x - (adjustScaledWidth * cosA - adjustedScaledHeight * sinA);
      }

      this.set({
        top,
        left,
        angle: connector.alignLabels ? angle : 0,
      });

      this.setCoords();
    }

    useUprightAngle(angle: number) {
      return angle > 90 || angle < -90 ? angle - 180 : angle;
    }

    _findShapeTargets(canvas: Canvas, pointer: fabric.Point) {
      // Translate pointer for viewport.
      const vptPointer = fabric.util.transformPoint(
        pointer,
        canvas.viewportTransform!
      );

      const objects = canvas.getObjects().filter(isConnector);
      let i = objects.length;

      while (i--) {
        const objToCheck = objects[i];
        if (objToCheck.containsPoint(vptPointer)) return objToCheck;
      }
    }

    dragLabel(event) {
      // Ignore drag events when dragged as part of group
      if (this.canvas?.getActiveObject()?.type === 'activeSelection') return;

      const { pointer, transform } = event;

      // In certain cases (e.g., copy/paste), 'moving' still fires, but not
      // necessarily with a transform or pointer
      if (!pointer || !transform) {
        return;
      }

      // If dragged over a connector, become a label
      if (!this.connector) {
        this.connector = this._findShapeTargets(
          transform.target.canvas,
          pointer
        );
        if (this.connector) {
          const offset = this.connector.router.closestPointOnPath(
            this.connector,
            pointer
          );
          this.initLabel(this.connector, offset);
        }
      }

      // If already a label, update position along connector
      if (this.connector?.nearPoint(pointer)) {
        // Adjust for the offset between the pointer and the center of the label.
        const transformedPointer = {
          x: pointer.x + this.getScaledWidth() / 2 - transform.offsetX,
          y: pointer.y + this.getScaledHeight() / 2 - transform.offsetY,
        };
        const offset = this.connector.router.closestPointOnPath(
          this.connector,
          transformedPointer
        );
        this.set('labelPosition', offset);
        this.updateLabelPositionAndAngle(this.connector);
        this.connector.canvas.setActiveObject(this.connector);
      } else if (this.connector) {
        // Label was dragged off a connector, so stop being a label
        this.connector.canvas.discardActiveObject();
        this.clearLabel();
      }
    }

    initLabel(connector: Connector, t: number | null = 0.5, angle = 0) {
      // Use existing label's position, for cases such as undo
      const position = this.labelPosition ? this.labelPosition : t;
      this.connector = connector;
      this.connector.addLabel(this);
      this.setLabelFields(connector.id, position!, angle);
      this.setEvents();
      // Set initial position and angle
      this.updateLabelPositionAndAngle(this.connector);
      if (connector.locked) {
        this.evented = false;
      }
    }

    clearLabel() {
      this.removeConnectorLabel();
      this.clearLabelFields();
      this.clearEvents();
    }

    getDefaultControls(): typeof ShapeBase.shapeControls {
      // @ts-expect-error we assume this is a Shape object, and TS also doesn't like us accessing
      // the constructor like this
      return this.shape.constructor.shapeControls;
    }

    // Prepare ShapeText to become a label
    setLabelFields(connectorId: RecordId, t: number, angle = 0) {
      this.set({
        connectorId,
        labelPosition: t,
        labelOrientation: 'inline',
        angle,
      });
      // remove all controls for text, since size is controlled by font size. For other label
      // shapes, keep resize controls
      this.controls = {};
      if (!isShape(this) || this.shapeName !== 'text') {
        const defaults = this.getDefaultControls();
        for (const key of ['tl', 'tr', 'bl', 'br', 'mtr']) {
          this.controls[key] = defaults[key];
        }
      }
    }

    // Clear fields and reset controls
    clearLabelFields() {
      this.set({
        connectorId: null,
        labelPosition: null,
      });
      this.controls = this.getDefaultControls();
    }

    setEvents() {
      this.clearEvents();
      this.on('removed', this.removalHandler);
      this.on('mousedblclick', this.doubleClickHandler);
      this.on('deselected', this.deselectHandler);
    }

    clearEvents() {
      this.off('removed', this.removalHandler);
      this.off('mousedblclick', this.doubleClickHandler);
      this.off('deselected', this.deselectHandler);
    }

    removalHandler() {
      this.connector?.removeLabel(this);
    }

    doubleClickHandler() {
      if (!this.connector || this.canvas.readOnly) {
        return;
      }
      const { controller, canvas } = this.connector;
      canvas.setActiveObject(
        controller.dataStoreObjects.findRecordById(this.id)
      );
    }

    // Ensure shape updates with new PNO after text edit is done
    deselectHandler() {
      this.updateLabelPositionAndAngle(this.connector!);
    }

    _set(keyOrPath: KeyPath, value: any): fabric.Object {
      const shouldInitLabel =
        keyOrPath === 'connectorId' &&
        value != null &&
        this.connectorId !== value;

      // Remove from previous connector
      if (shouldInitLabel) {
        this.removeConnectorLabel();
      }

      super._set(keyOrPath, value);

      // In cases such as undo, implicitly detect that label was attached
      if (shouldInitLabel) {
        const connector =
          this.controller.dataStoreObjects.findRecordById(value);
        if (connector == null || !isConnector(connector)) return this;

        this.initLabel(connector);
      }

      return this;
    }
  };
}

export default LabelBehaviors;
